import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import {
  isError, isFetching, isFinishedUpdating, isSuccess, isUpdating, ResourcesItem,
} from 'redux-and-the-rest';
import { Error } from '../../models/error';

import { SchoolModel } from '../../models/schoolModel';
import { getOrFetchSchool, updateSchool } from '../../redux/reducers/schoolReducer';
import { ApplicationState } from '../../redux/types';
import { buildErrorsDictionary, ErrorMap } from '../../utils/ErrorsUtils';
import LoadingIndicator from '../shared/LoadingIndicator';
import TextInput from '../shared/TextInput';

interface SchoolFormFields {
  name: string;
  code: string;
  passcode: string;
}

interface Props {
  onSubmit(schoolData: SchoolFormFields): void;
  school: ResourcesItem<SchoolModel>;
}

type SchoolErrorField = keyof SchoolFormFields;

const PASSCODE_MIN_LENGTH = 3;

const School = ({ school, onSubmit }: Props): React.ReactElement => {
  const {
    register, handleSubmit, formState: { errors, isSubmitted }, setValue, setError,
  } = useForm<SchoolFormFields>();

  const [successMessage, setSuccessMessage] = useState<string | undefined>();
  const isUpdatingSchool = isUpdating(school);

  const submit = handleSubmit((formData) => {
    setSuccessMessage(undefined);
    onSubmit(formData);
  });

  useEffect(() => {
    /**
     * When the School status changes to Success, whether via a Fetch or
     * an Update, update the form fields.
     */
    if (isSuccess(school)) {
      setValue('name', school.values.name);
      setValue('code', school.values.code);
      setValue('passcode', school.values.passcode);
    }
  }, [school.status.type]);

  useEffect(() => {
    if (isFinishedUpdating(school) && isSubmitted) {
      if (isError(school)) {
        const { status: { errors: remoteErrors } } = school;
        const errorMap: ErrorMap<SchoolErrorField> = buildErrorsDictionary<SchoolErrorField>(remoteErrors);

        // Map API errors to our fields via the pointer
        errorMap.forEach((error: Error, key: SchoolErrorField) => setError(key, {
          message: error.detail,
        }));
      } else if (isSubmitted) {
        /**
         * If we return to Edit school, the entity is in a FinishedUpdating state.
         * Ensure the success message is only displayed if the form has been submitted
         */
        setSuccessMessage('Your Passcode has been successfully updated');
      }
    }
  }, [school.status.type]);

  return (
    <div className="school loadable">
      {isFetching(school) ? (
        <LoadingIndicator />
      ) : (
        <form className="form" onSubmit={submit}>
          <div className="form-section">
            <div className="form-group">
              <TextInput
                disable
                label="School Name"
                useFormRegisterReturn={register('name')}
                error={errors.name?.message}
              />
            </div>

            <div className="form-group form-group-info">
              Your students will need to enter the following code to log in
            </div>

            <div className="form-group">
              <TextInput
                disable
                label="School Code"
                useFormRegisterReturn={register('code')}
                error={errors.code?.message}
                hint="This is your unique school code which will not change."
              />
            </div>

            <div className="form-group">
              <TextInput
                require
                type="passcode"
                label="School Passcode"
                useFormRegisterReturn={register('passcode', {
                  required: 'A School Passcode is required',
                  minLength: {
                    value: PASSCODE_MIN_LENGTH,
                    message: `Passcode is too short (minimum is ${PASSCODE_MIN_LENGTH} characters`,
                  },
                })}
                error={errors.passcode?.message}
              />
            </div>
          </div>

          {successMessage && (
            <div className="form-section form-message success">
              {successMessage}
            </div>
          )}

          <div className="form-section form-actions">
            <button className="button primary" type="submit" disabled={isUpdatingSchool}>
              Change Passcode
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

const mapStateToProps = (
  { schools }: ApplicationState,
  { match: { params: { id } } }: RouteComponentProps<{ id: string; }>,
) => ({
  onSubmit: ({ name, code, passcode }: SchoolFormFields) => {
    updateSchool({
      id,
    }, {
      id,
      passcode,
      name,
      code,
      type: 'school',
    });
  },
  school: getOrFetchSchool(schools, { id }),
});

export default withRouter(connect(mapStateToProps)(School));
